/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import queryString from 'query-string';
import type {
	SchemaId,
	CmdbObjectTypeId,
	CmdbObjectId,
} from '@jira/servicedesk__insight-common-types-shared-types/src/toCmdbObjectTypeId.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { SCHEMA_PAGE_QUERY_PARAMS } from './useUpdateQueryParams.tsx';

// Please try to keep this package very light-weight, since it is used in critical code such as routes.
// By convention, routes start with a '/' and do not including a trailing slash.
export const CREATE_SERVICE_URL = '/jira/servicedesk/service-modal';
export const ASSETS_APP_ROUTE_PREFIX = '/jira/assets';
export const JSM_ASSETS_ROUTE_PREFIX = '/jira/servicedesk/assets';
export const INSIGHT_ROUTE_PREFIX = '/jira/servicedesk/insight';
export const ASSETS_APP_OBJECT_ROUTE_PREFIX = '/jira/assets/object';

export const isInsightRoute = (pathname: string): boolean =>
	pathname.includes(INSIGHT_ROUTE_PREFIX);

export const getCreateServiceUrl = (): string => CREATE_SERVICE_URL;

export const getRootPath = (): '/jira/assets' | '/jira/servicedesk/assets' =>
	fg('assets_as_an_app_v2') ? ASSETS_APP_ROUTE_PREFIX : JSM_ASSETS_ROUTE_PREFIX;

export const getLandingPageUrl = (): '/jira/assets' | '/jira/servicedesk/assets' => getRootPath();

export const getGlobalConfigUrl = (): string => `${getRootPath()}/configure/global`;

export const getAssetsSettingsUrl = (): string => `${ASSETS_APP_ROUTE_PREFIX}/configure/global`;

export const getDataManagerUrl = (): string => `${getRootPath()}/data-manager/`;

export const getDataManagerGettingStartedUrl = (): string =>
	`${ASSETS_APP_ROUTE_PREFIX}/data-manager/getting-started`;

export const getDataManagerObjectClassesUrl = (): string =>
	`${ASSETS_APP_ROUTE_PREFIX}/data-manager/object-classes`;

export const getDataManagerVisualizationUrl = (): string =>
	`${ASSETS_APP_ROUTE_PREFIX}/data-manager/#/dashboards`;

export const getDataManagerAdaptersUrl = (): string => `${getRootPath()}/data-manager/adapters/`;

export const getAssetsReportsUrl = (): string => `${getRootPath()}/reports`;

export const getAssetsDashboardsUrl = (): string => `${getRootPath()}/dashboards`;

export const getDataManagerDiscoverUrl = (): string =>
	`${ASSETS_APP_ROUTE_PREFIX}/discover/data-manager`;

export const getDataManagerSettingsUrl = (): string =>
	`${ASSETS_APP_ROUTE_PREFIX}/configure/global?menu=data-manager`;

export const getAssetsAnalyticsCustomMetricsUrl = (): string =>
	`${getRootPath()}/analytics/data-view/create`;

export const getAssetsReportUrl = (reportId: string): string =>
	`${getAssetsReportsUrl()}/${reportId}`;

export const getAssetsSearchResultsUrl = (query?: string): string =>
	`${getRootPath()}/search${query ? `?${query}` : ''}`;

export const getAssetsForYouUrl = (): string => `${getRootPath()}/for-you`;

export const createObjectSchemasUrl = (): string => `${getRootPath()}/object-schemas`;

export const createObjectSchemaUrl = (objectSchemaId: SchemaId): string =>
	`${getRootPath()}/object-schema/${objectSchemaId}`;

export const createObjectTypeUrl = (
	objectSchemaId: SchemaId,
	objectTypeId: CmdbObjectTypeId,
): string =>
	`${createObjectSchemaUrl(objectSchemaId)}?${
		SCHEMA_PAGE_QUERY_PARAMS.objectTypeId
	}=${objectTypeId}`;

export const createObjectSchemaConfigUrl = (objectSchemaId: SchemaId): string =>
	`${getRootPath()}/configure/object-schema/${objectSchemaId}`;

export const createObjectDetailUrl = (objectId: CmdbObjectId): string =>
	`${getRootPath()}/object/${objectId}`;

export const createObjectTypeSettingsUrl = (objectTypeId: CmdbObjectTypeId): string =>
	`${getRootPath()}/configure/object-type/${objectTypeId}`;

export const createObjectTypeListWithObjectSelectedUrl = (
	objectSchemaId: SchemaId,
	objectTypeId: CmdbObjectTypeId,
	objectId: CmdbObjectId,
): string => `${createObjectTypeUrl(objectSchemaId, objectTypeId)}&view=list&objectId=${objectId}`;

export const createSearchUrl = (queryParams: Record<string, unknown> = {}): string => {
	const base = `${ASSETS_APP_ROUTE_PREFIX}/search`;
	if (Object.keys(queryParams).length > 0) {
		const encodedParams = queryString.stringify(queryParams);
		return `${base}?${encodedParams}`;
	}
	return base;
};

export const getFeatureUsagePageUrl = ({
	showChangeLimitModal,
	hasAssetsEntitlement = false,
}: {
	showChangeLimitModal?: boolean;
	hasAssetsEntitlement?: boolean;
} = {}): string =>
	hasAssetsEntitlement && fg('aurum-677-assets-usage-migration')
		? '/jira/assets/configure/global?menu=app-usage'
		: `/jira/settings/products/servicedesk/feature-usage?feature=assets${showChangeLimitModal ? '&changeLimit=true' : ''}`;

export const getFeatureUsageVirtualAgentPageUrl = ({
	showChangeLimitModal,
}: {
	showChangeLimitModal?: boolean;
} = {}): string =>
	`/jira/settings/products/servicedesk/feature-usage?feature=virtual-agent${showChangeLimitModal ? '&changeLimit=true' : ''}`;
